/**
 * Extracts specific CSS properties from an inline style string
 * @param styleString - The inline style string to parse (e.g., "font-size: 12px; color: red;")
 * @param propertiesToExtract - Array of CSS property names to extract (e.g., ['font-size', 'font-family'])
 * @param fallbackStyle - Optional fallback style to use if no properties are found
 * @returns Object containing the extracted styles and formatted string
 */
export interface ExtractedStyles {
  styles: Record<string, string>
  formattedString: string
}

export const extractInlineStyles = (
  styleString: string,
  propertiesToExtract: string[],
  fallbackStyle = 'font-size: 11pt;',
): ExtractedStyles => {
  if (!styleString || !propertiesToExtract.length) {
    return {
      styles: {},
      formattedString: fallbackStyle,
    }
  }

  const extractedStyles: Record<string, string> = {}
  const formattedArray: string[] = []

  // Create a regex pattern that matches any of the specified properties
  const propertyPattern = propertiesToExtract
    .map((prop) => prop.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')
  const styleRegex = new RegExp(`(${propertyPattern})\\s*:\\s*([^;]+);?`, 'gi')

  let match: RegExpExecArray | null

  while ((match = styleRegex.exec(styleString)) !== null) {
    const property = match[1].trim()
    const value = match[2].trim()

    extractedStyles[property] = value
    formattedArray.push(`${property}: ${value}`)
  }

  // Use extracted styles or fallback
  const formattedString =
    formattedArray.length > 0 ? formattedArray.join('; ') + ';' : fallbackStyle

  return {
    styles: extractedStyles,
    formattedString,
  }
}
